<template>
  <section>
    <div class="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
      <div>
        <div class="eyebrow">Obrigações e pagamentos</div>
        <h1 class="mt-2 text-3xl font-extrabold text-[#102a5a]">Obrigações</h1>
        <p class="mt-1 text-sm text-slate-500">Consulte documentos financeiros, vencimentos, valores pagos e saldos em aberto.</p>
      </div>
      <router-link v-if="canWrite" class="btn-primary" to="/obrigacoes/nova"><AppIcon name="plus" class="h-4 w-4" />Incluir documento</router-link>
    </div>

    <section class="card obligation-filter-panel mb-3">
      <form class="obligation-filter-grid" @submit.prevent="applyFilters">
        <label class="obligation-search-field">
          <span class="label">Pesquisar</span>
          <span class="search-input-wrap"><AppIcon name="search" /><input v-model.trim="filters.search" class="input" type="search" placeholder="Número, origem, entidade ou descrição"></span>
        </label>
        <label><span class="label">Situação</span><select v-model.number="filters.statusId" class="input"><option :value="0">Todas</option><option v-for="option in options.statuses" :key="option.id" :value="option.id">{{ optionLabel(option) }}</option></select></label>
        <label><span class="label">Tipo de documento</span><select v-model.number="filters.documentTypeId" class="input"><option :value="0">Todos</option><option v-for="option in options.documentTypes" :key="option.id" :value="option.id">{{ optionLabel(option) }}</option></select></label>
        <label><span class="label">Entidade proprietária</span><select v-model.number="filters.ownerId" class="input"><option :value="0">Todas</option><option v-for="option in options.entities" :key="option.id" :value="option.id">{{ optionLabel(option) }}</option></select></label>
        <label><span class="label">Emissão inicial</span><input v-model="filters.issueDateFrom" class="input" type="date"></label>
        <label><span class="label">Emissão final</span><input v-model="filters.issueDateTo" class="input" type="date"></label>
        <div class="obligation-filter-actions">
          <button class="btn-secondary" type="button" :disabled="loading" @click="clearFilters">Limpar</button>
          <button class="btn-primary" type="submit" :disabled="loading"><AppIcon name="search" class="h-4 w-4" />Buscar</button>
        </div>
      </form>
    </section>

    <section class="card overflow-hidden">
      <div class="flex flex-col gap-2 border-b border-stone-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div><h2 class="text-lg font-bold">Documentos financeiros</h2><p class="text-sm text-slate-500">{{ totalCount }} registro{{ totalCount === 1 ? '' : 's' }} encontrado{{ totalCount === 1 ? '' : 's' }}.</p></div>
        <span v-if="loading" class="loading-label"><span class="loading-spinner" />Atualizando</span>
      </div>
      <div class="table-scroll overflow-x-auto">
        <table class="tbl obligation-table">
          <thead><tr>
            <th><button class="sort-button" type="button" @click="sort('documentNumber')">Documento <span class="sort-indicator" :class="{ active: sortBy === 'documentNumber' }">{{ sortIcon('documentNumber') }}</span></button></th>
            <th><button class="sort-button" type="button" @click="sort('documentType')">Tipo <span class="sort-indicator" :class="{ active: sortBy === 'documentType' }">{{ sortIcon('documentType') }}</span></button></th>
            <th><button class="sort-button" type="button" @click="sort('owner')">Proprietária <span class="sort-indicator" :class="{ active: sortBy === 'owner' }">{{ sortIcon('owner') }}</span></button></th>
            <th><button class="sort-button" type="button" @click="sort('counterparty')">Contraparte <span class="sort-indicator" :class="{ active: sortBy === 'counterparty' }">{{ sortIcon('counterparty') }}</span></button></th>
            <th><button class="sort-button" type="button" @click="sort('issueDate')">Emissão <span class="sort-indicator" :class="{ active: sortBy === 'issueDate' }">{{ sortIcon('issueDate') }}</span></button></th>
            <th><button class="sort-button" type="button" @click="sort('nextDueDate')">Próximo vencimento <span class="sort-indicator" :class="{ active: sortBy === 'nextDueDate' }">{{ sortIcon('nextDueDate') }}</span></button></th>
            <th class="numeric-heading"><button class="sort-button justify-end" type="button" @click="sort('totalAmount')">Total <span class="sort-indicator" :class="{ active: sortBy === 'totalAmount' }">{{ sortIcon('totalAmount') }}</span></button></th>
            <th class="numeric-heading"><button class="sort-button justify-end" type="button" @click="sort('outstandingAmount')">Saldo <span class="sort-indicator" :class="{ active: sortBy === 'outstandingAmount' }">{{ sortIcon('outstandingAmount') }}</span></button></th>
            <th><button class="sort-button" type="button" @click="sort('status')">Situação <span class="sort-indicator" :class="{ active: sortBy === 'status' }">{{ sortIcon('status') }}</span></button></th>
            <th class="actions-heading">Ações</th>
          </tr></thead>
          <tbody>
            <tr v-if="loading && items.length === 0"><td colspan="10" class="empty-state">Carregando obrigações...</td></tr>
            <tr v-else-if="items.length === 0"><td colspan="10" class="empty-state">Nenhuma obrigação encontrada.</td></tr>
            <tr v-for="item in items" v-else :key="item.id">
              <td data-label="Documento"><router-link class="document-link" :to="`/obrigacoes/${item.id}`">{{ item.documentNumber }}</router-link><small>{{ item.installmentCount }} parcela{{ item.installmentCount === 1 ? '' : 's' }}</small></td>
              <td data-label="Tipo">{{ item.documentTypeName }}</td>
              <td data-label="Proprietária">{{ item.ownerName }}</td>
              <td data-label="Contraparte">{{ item.counterpartyName }}</td>
              <td data-label="Emissão">{{ formatDate(item.issueDate) }}</td>
              <td data-label="Próximo vencimento" :class="{ 'overdue-value': isOverdue(item) }">{{ formatDate(item.nextDueDate) }}</td>
              <td data-label="Total" class="numeric-cell">{{ formatCurrency(item.totalAmount) }}</td>
              <td data-label="Saldo" class="numeric-cell"><strong>{{ formatCurrency(item.outstandingAmount) }}</strong></td>
              <td data-label="Situação"><span class="obligation-status" :class="statusClass(item.statusCode)">{{ item.statusName }}</span></td>
              <td data-label="Ações" class="actions-cell"><span class="row-actions">
                <router-link class="row-action view" :to="`/obrigacoes/${item.id}`" title="Visualizar" aria-label="Visualizar"><AppIcon name="eye" /></router-link>
                <router-link v-if="canWrite && item.statusCode === 'DRAFT'" class="row-action edit" :to="`/obrigacoes/${item.id}/editar`" title="Continuar edição" aria-label="Continuar edição"><AppIcon name="pencil" /></router-link>
              </span></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="pagination-footer flex flex-col gap-4 border-t border-stone-200 px-5 py-4 text-sm text-slate-500 lg:flex-row lg:items-center lg:justify-between">
        <span>Exibindo {{ displayStart }} a {{ displayEnd }} de {{ totalCount }}</span>
        <div class="pagination-controls">
          <label>Linhas por página<select v-model.number="pageSize" class="pagination-select" @change="changePageSize"><option v-for="size in pageSizeOptions" :key="size" :value="size">{{ size }}</option></select></label>
          <span>Página {{ totalPages === 0 ? 0 : page }} de {{ totalPages }}</span>
          <div class="pagination-buttons">
            <button type="button" aria-label="Página anterior" :disabled="page <= 1 || loading" @click="goToPage(page - 1)"><AppIcon name="chevronLeft" /></button>
            <button class="current-page" type="button" aria-current="page">{{ totalPages === 0 ? 0 : page }}</button>
            <button type="button" aria-label="Próxima página" :disabled="page >= totalPages || loading" @click="goToPage(page + 1)"><AppIcon name="chevronRight" /></button>
          </div>
        </div>
      </div>
    </section>
  </section>
</template>

<script>
import Swal from 'sweetalert2'
import AppIcon from '../components/AppIcon.vue'
import service from '../services/financialDocumentService'
import accessService from '../services/financialAccessService'

const localToday = () => {
  const value = new Date()
  return new Date(value.getTime() - (value.getTimezoneOffset() * 60000)).toISOString().slice(0, 10)
}

export default {
  name: 'FinancialDocumentsView',
  components: { AppIcon },
  data: () => ({
    items: [],
    options: { documentTypes: [], entities: [], statuses: [], itemTypes: [] },
    filters: { search: '', statusId: 0, documentTypeId: 0, ownerId: 0, issueDateFrom: '', issueDateTo: '' },
    page: 1,
    pageSize: 20,
    pageSizeOptions: [20, 40, 80, 100],
    totalCount: 0,
    totalPages: 0,
    sortBy: 'issueDate',
    sortDirection: 'desc',
    loading: false,
    canWrite: false
  }),
  computed: {
    displayStart () { return this.totalCount === 0 ? 0 : ((this.page - 1) * this.pageSize) + 1 },
    displayEnd () { return Math.min(this.page * this.pageSize, this.totalCount) }
  },
  async created () {
    this.canWrite = await accessService.has('OBLIGATIONS', 2)
    await this.loadOptions()
    await this.load()
  },
  methods: {
    async loadOptions () {
      try {
        this.options = await service.getOptions()
      } catch (error) {
        await this.showError(error)
      }
    },
    async load () {
      this.loading = true
      try {
        const result = await service.getPaged({
          page: this.page,
          pageSize: this.pageSize,
          search: this.filters.search || undefined,
          statusId: this.filters.statusId || undefined,
          documentTypeId: this.filters.documentTypeId || undefined,
          ownerId: this.filters.ownerId || undefined,
          issueDateFrom: this.filters.issueDateFrom || undefined,
          issueDateTo: this.filters.issueDateTo || undefined,
          sortBy: this.sortBy,
          sortDirection: this.sortDirection
        })
        this.items = result.items
        this.totalCount = result.totalCount
        this.totalPages = result.totalPages
      } catch (error) {
        await this.showError(error)
      } finally {
        this.loading = false
      }
    },
    applyFilters () { this.page = 1; this.load() },
    clearFilters () {
      this.filters = { search: '', statusId: 0, documentTypeId: 0, ownerId: 0, issueDateFrom: '', issueDateTo: '' }
      this.page = 1
      this.load()
    },
    sort (column) {
      if (this.sortBy === column) this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      else { this.sortBy = column; this.sortDirection = 'asc' }
      this.page = 1
      this.load()
    },
    sortIcon (column) { return this.sortBy !== column ? '↕' : (this.sortDirection === 'asc' ? '↑' : '↓') },
    changePageSize () { this.page = 1; this.load() },
    goToPage (page) { if (page < 1 || page > this.totalPages || page === this.page) return; this.page = page; this.load() },
    formatDate (value) { return value ? new Intl.DateTimeFormat('pt-BR').format(new Date(`${String(value).slice(0, 10)}T12:00:00`)) : '—' },
    formatCurrency (value) { return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0)) },
    isOverdue (item) {
      if (!item.nextDueDate || Number(item.outstandingAmount) <= 0) return false
      return String(item.nextDueDate).slice(0, 10) < localToday()
    },
    statusClass (code) { return `status-${String(code || '').toLowerCase().replaceAll('_', '-')}` },
    optionLabel (option) { return option.isActive ? option.name : `${option.name} (inativo)` },
    async showError (error) {
      const validationErrors = error?.response?.data?.errors
      const firstValidation = validationErrors ? Object.values(validationErrors).flat()[0] : null
      const message = firstValidation || error?.response?.data?.title || error?.response?.data?.detail || 'Não foi possível carregar as obrigações.'
      await Swal.fire({ title: 'Atenção', text: message, icon: 'error', confirmButtonColor: '#e77900' })
    }
  }
}
</script>
